import moment from 'moment-timezone'
import { defaultValues } from './constants'

type ScheduleSide = { seconds?: number | null; scheduled?: boolean } | null

const NOT_SCHEDULED = 'Not Scheduled'

/**
 * One side of a schedule window, or null when it has no numeric seconds.
 * Main app: formatSingleDate inside DateTimeFormatNew (utils/formatTime.ts)
 * The time is shown only when the side is scheduled. The web leaves a trailing
 * space there, which the browser collapses, so it is dropped here.
 */
const formatScheduleSide = (
  side: ScheduleSide | undefined,
  timezone: string
): string | null => {
  if (typeof side?.seconds !== 'number') return null
  const ms = side.seconds > 1e12 ? side.seconds : side.seconds * 1000
  const date = moment(ms).tz(timezone)
  return side.scheduled
    ? `${date.format('ll')} ${date.format('h:mm A')}`
    : date.format('ll')
}

/**
 * Format a stop's schedule window exactly like the web's Dispatch tab stop row
 * (DispatchFormContent/components/StopRow.tsx): DateTimeFormatNew(start) - DateTimeFormatNew(end)
 * - A side with no numeric seconds reads 'Not Scheduled'
 * - Each side shows its date, and its time only when its `scheduled` flag is true
 * - Seconds and milliseconds are both accepted
 * - Zone: the given one, else America/New_York; a name moment does not know falls back to UTC
 * - No zone label
 * @returns e.g. "Jan 8, 2026 8:00 AM - Jan 8, 2026 12:00 PM", or 'Not Scheduled - Not Scheduled' when both sides are missing
 * @example
 * formatScheduleWindow({ seconds: 1767880800, scheduled: true }, { seconds: 1767895200, scheduled: true }, 'America/Chicago')
 * // "Jan 8, 2026 8:00 AM - Jan 8, 2026 12:00 PM"
 */
export const formatScheduleWindow = (
  scheduleStart?: ScheduleSide,
  scheduleEnd?: ScheduleSide,
  timezone?: string | null
): string => {
  try {
    const zoneName = timezone ?? defaultValues.DEFAULT_TIMEZONE
    const tz = moment.tz.zone(zoneName) ? zoneName : 'UTC'
    const startText = formatScheduleSide(scheduleStart, tz)
    const endText = formatScheduleSide(scheduleEnd, tz)
    return `${startText ?? NOT_SCHEDULED} - ${endText ?? NOT_SCHEDULED}`
  } catch {
    return `${NOT_SCHEDULED} - ${NOT_SCHEDULED}`
  }
}

/**
 * Format check-in/out timestamp with full date + time
 * Pattern from main app: DateTimeFormat in CheckInOut.tsx ('ll' date, 'h:mm A' time)
 * Shows complete date because driver may check in/out on different date than scheduled
 * @param timestamp - Unix timestamp (seconds or milliseconds)
 * @param timezone - Timezone string
 * @returns Formatted date+time "Jan 19, 2026 5:00 PM" or empty string
 * @example
 * formatCheckTime(1767881700, 'America/Chicago') // "Jan 8, 2026 8:15 AM"
 */
export const formatCheckTime = (
  timestamp?: number | null,
  timezone?: string | null
): string => {
  try {
    if (!timestamp) return ''
    // Main app: the stop views pass getTimeZoneUtil(stop) ?? 'America/New_York', and
    // DateTimeFormat (components/common/functions.tsx) uses UTC for a zone moment does not know
    const zoneName = timezone ?? defaultValues.DEFAULT_TIMEZONE
    const tz = moment.tz.zone(zoneName) ? zoneName : 'UTC'

    // Detect if timestamp is in milliseconds or seconds (main app pattern)
    const isMilliseconds = timestamp > 1e12
    const ms = isMilliseconds ? timestamp : timestamp * 1000

    // Show full date + time (driver may check in/out on different date than scheduled)
    return moment(ms).tz(tz).format('ll h:mm A')
  } catch {
    return ''
  }
}
